import {
  Report,
  ReportType,
} from '@/shared/api/protocol-ts/model/dto_report_pb';
import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';

const REPORT_TYPE_PRIORITY: ReportType[] = [
  ReportType.ReportType_CBCT_GP,
  ReportType.ReportType_Pano_Bitewings,
  ReportType.ReportType_Pano_GP,
  ReportType.ReportType_IOXRay_GP,
  ReportType.ReportType_CBCT_IOS_Superimposition,
  ReportType.ReportType_CBCT_Segmentation,
  ReportType.ReportType_StudioImplant,
  ReportType.ReportType_StudioOrtho,
  ReportType.ReportType_IOS_Segmentation,
];

const REPORTS_WITH_PANORAMIC_REFORMAT: ReportType[] = [
  ReportType.ReportType_CBCT_GP,
  ReportType.ReportType_CBCT_OrthoSlides,
];

const isReportCompleted = (report: Report): boolean => {
  // CBCT_OrthoSlides reports exist but aren't marked as completed by backend
  const isOrthoReport = report.Type === ReportType.ReportType_CBCT_OrthoSlides;
  return isOrthoReport || report.Status?.Completed === true;
};

const reportHasPreview = (report: Report): boolean => {
  // CBCT_OrthoSlides reports have preview in assets, not DisplayAssetID
  const isOrthoReport = report.Type === ReportType.ReportType_CBCT_OrthoSlides;
  return isOrthoReport || Boolean(report.DisplayAssetID);
};

const getNewestReport = (reports: Report[]): Report | undefined => {
  if (reports.length === 0) {
    return undefined;
  }

  return reports.reduce((newest, current) => {
    const newestTime = newest.Created?.At?.toDate().getTime() ?? 0;
    const currentTime = current.Created?.At?.toDate().getTime() ?? 0;
    return currentTime > newestTime ? current : newest;
  });
};

/**
 * Selects the best preview report for a study based on priority and completeness
 * @param reports - Array of reports for the study
 * @param studyType - Type of the study (CBCT, Pano, etc.)
 * @returns The selected report for preview or undefined if no suitable report found
 */
export const getStudyPreviewReport = (
  reports: Report[],
  studyType: StudyType,
): Report | undefined => {
  const completedReportsWithPreview = reports.filter(
    (report) => isReportCompleted(report) && reportHasPreview(report),
  );

  if (completedReportsWithPreview.length === 0) {
    return undefined;
  }

  const eligibleReports =
    studyType === StudyType.StudyType_CBCT
      ? completedReportsWithPreview.filter((report) =>
          REPORTS_WITH_PANORAMIC_REFORMAT.includes(report.Type),
        )
      : completedReportsWithPreview;

  if (eligibleReports.length === 0) {
    return undefined;
  }

  const findReportByPriority = REPORT_TYPE_PRIORITY.reduce<Report | undefined>(
    (foundReport, priorityType) => {
      if (foundReport) {
        return foundReport;
      }

      const reportsOfType = eligibleReports.filter(
        (report) => report.Type === priorityType,
      );

      return getNewestReport(reportsOfType) ?? foundReport;
    },
    undefined,
  );

  return findReportByPriority ?? getNewestReport(eligibleReports);
};
